import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `settings` domain — the four tabs of `/settings`.
 *
 * One tab is dangerous and three do nothing, which is the worst possible mix
 * to leave undocumented.
 *
 * Dangerous: the key/value tab edits `setting.settings` directly, and the rows
 * named `finance.config` — the platform default, plus one `finance.config:<agent
 * id>` per brand that has settings of its own — carry the emergency stops and the
 * default deposit/withdraw floors. Every deposit, withdrawal and bet re-reads the
 * resolved answer for its own brand through a 5-second cache
 * (packages/shared/src/clients/platform-switches.ts). DELETING a row does not
 * stop anything: `GET /finance-config` answers `{...DEFAULTS, ...platform,
 * ...brand}` and DEFAULTS has deposit, withdraw and betting all `true` and both
 * floors 0 (apps/setting/src/routes/finance-config.ts). Deleting the BRAND row is
 * in fact the supported way back to the platform default, and the Finance
 * settings dialog has a button for it. The same fallback swallows a malformed
 * value, because the edit dialog stores anything that fails `JSON.parse` as a
 * plain string (pages/settings/SettingsTab.tsx:82-87).
 *
 * Inert: nothing outside svc-setting reads a feature flag, calls the IP
 * matcher, or looks at a maintenance window — verified by grepping every app
 * and package for the three resources. All three are `records-intent`.
 *
 * Nothing in svc-setting emits an audit event at all, on any tab.
 */
export const settingsGuide: DomainGuide = {
  domain: 'settings',
  pages: [
    {
      id: 'settings.keyValues',
      route: '/settings → Settings',
      // Gateway: reads setting:read, writes setting:update
      // (api-gateway/src/upstreams.ts:425-426).
      permission: 'setting:read',
      status: 'live',
      fields: [
        { key: 'key', kind: 'text' },
        { key: 'scope', kind: 'text' },
        { key: 'value', kind: 'text' },
        { key: 'updatedAt', kind: 'date' },
      ],
      cautionCount: 7,
      warningCount: 4,
    },
    {
      id: 'settings.featureFlags',
      route: '/settings → Feature flags',
      permission: 'setting:read',
      status: 'records-intent',
      fields: [
        { key: 'key', kind: 'text' },
        { key: 'enabled', kind: 'status' },
        { key: 'rollout', kind: 'percent' },
        { key: 'description', kind: 'text' },
        { key: 'updatedAt', kind: 'date' },
      ],
      cautionCount: 5,
      warningCount: 0,
    },
    {
      id: 'settings.ipPolicies',
      route: '/settings → IP policies',
      permission: 'setting:read',
      status: 'records-intent',
      fields: [
        { key: 'ipCidr', kind: 'text' },
        { key: 'action', kind: 'status' },
        { key: 'scope', kind: 'text' },
        { key: 'note', kind: 'text' },
        { key: 'status', kind: 'status' },
        { key: 'createdAt', kind: 'date' },
      ],
      cautionCount: 7,
      warningCount: 1,
    },
    {
      id: 'settings.maintenance',
      route: '/settings → Maintenance',
      permission: 'setting:read',
      status: 'records-intent',
      fields: [
        { key: 'scope', kind: 'text' },
        { key: 'message', kind: 'text' },
        { key: 'startsAt', kind: 'date' },
        { key: 'endsAt', kind: 'date' },
        { key: 'status', kind: 'status' },
      ],
      cautionCount: 6,
      warningCount: 0,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
